import { useCallback } from 'react';
import { useCampaignSectionContext } from 'sections/onsite-campaigns/context/CampaignSectionContext';
import { useCampaignEditorContext } from '../contexts/CampaignEditorContext';
import useNotifications from 'core/notifications/useNotifications';
import { useUpsertCampaignMutation } from 'sections/onsite-campaigns/hooks/useUpsertCampaignMutation';
import { useOnsiteCampaignUrlParams } from 'sections/onsite-campaigns/hooks/useOnsiteCampaignUrlParams';
import { useWorkflowForm } from '../tabs/CampaignWorkflowTab/contexts/CampaignWorkflowFormContext';
import { CampaignStateToResponseAdapter } from '../infrastructure/Adapters/CampaignStateToResponseAdapter/CampaignStateToResponseAdapter';
import { failedRulesToStateValues } from './failedRulesToStateValues';
import { useFailedRulesToStateMapper } from './useFailedRulesToStateMapper';
import { Workflow } from '../domain/Workflow/Workflow';
import { FlowService } from '../domain/Services/FlowService';
import { useWorkFlowCanvas } from '../tabs/CampaignWorkflowTab/contexts/CampaignFlowProvider';
import { CampaignEditorState } from '../contexts/CampaignEditorState';
import { OnsiteMessageSegment } from 'sections/onsite-campaigns/types';
import { useFeatureFlags } from 'core/app/AppContext';
import { FlagService } from '../domain/Services/FlagService';

export const useSaveWorkflow = () => {
  const { flagEnabled } = useFeatureFlags();
  const { showNotification } = useNotifications();
  const { siteId } = useOnsiteCampaignUrlParams();
  const upsertCampaignMutation = useUpsertCampaignMutation(siteId);

  const { state: SectionState } = useCampaignSectionContext();
  const { state: campaignEditorState } = useCampaignEditorContext();
  const { dispatch } = useCampaignEditorContext();
  const { state: CanvasState } = useWorkFlowCanvas();
  const { actions } = useWorkflowForm();

  const mapper = useFailedRulesToStateMapper();

  const onSave = useCallback(() => {
    const failedValidation = Workflow.createFromState(
      new FlowService(CanvasState),
      new FlagService(flagEnabled),
      (campaignEditorState.edit as CampaignEditorState) ?? campaignEditorState,
    ).saveWorkflow();

    if (failedValidation?.length) {
      const { errors, validationResults } = failedRulesToStateValues(failedValidation, mapper);

      const mappedValues = errors.reduce((result, item) => {
        const key = Object.keys(item)[0];
        result[key] = item[key];
        return result;
      }, {});

      actions.setErrors(mappedValues);
      actions.setValidationResults(validationResults, 'save');

      return;
    }

    const request = new CampaignStateToResponseAdapter(
      SectionState,
      campaignEditorState,
      (campaignEditorState.edit as CampaignEditorState) ?? campaignEditorState,
      CanvasState,
      { isLiveTrendsEnabled: flagEnabled('ld-trends') },
    );

    request['type'] = 'EAC';

    upsertCampaignMutation.mutate(request);

    //To sync workflow box ID with split test ID generator, we should fire an action to sync split test ID in split test reducer
    request?.onsiteMessageSegments?.forEach((segment: OnsiteMessageSegment) => {
      if (segment?.splitTestData?.id) {
        dispatch({
          type: 'UPDATE_SPLIT_TEST_ID',
          payload: {
            id: segment?.segmentId,
            splitTestId: segment.splitTestData?.id,
          },
        });
      }
    });

    dispatch({ type: 'SAVE_CAMPAIGN' });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignEditorState, CanvasState, SectionState, showNotification, upsertCampaignMutation]);

  return onSave;
};
